import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, completePartnerSetup } from '../lib/identityAuth';
import { BrandLogo } from '../components/BrandLogo';
import { PasswordField } from '../components/PasswordField';
import { config } from '../lib/config';

/** HQ-created partners set password via emailed setup token. */
export function PartnerSetupPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < config.passwordMinLength) {
      setError(`Password must be at least ${config.passwordMinLength} characters.`);
      return;
    }
    setLoading(true);
    try {
      await completePartnerSetup(token.trim(), password);
      navigate('/partner/login', {
        replace: true,
        state: { notice: 'Account ready. Sign in with your new password.' },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Setup failed');
      } else {
        setError(err instanceof Error ? err.message : 'Setup failed');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <BrandLogo />
        <h1>Partner account setup</h1>
        <p className="login-card__copy">
          Set a permanent password for your Sense partner account using the link from your invite
          email.
        </p>
        <form className="login-form" onSubmit={handleSubmit}>
          {!searchParams.get('token') ? (
            <label>
              <span>Setup token</span>
              <input value={token} onChange={(e) => setToken(e.target.value)} required />
            </label>
          ) : null}
          <PasswordField
            label="New password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
          />
          <PasswordField
            label="Confirm password"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            required
          />
          {error ? <div className="form-error">{error}</div> : null}
          <button className="primary-button" type="submit" disabled={loading || !token.trim()}>
            {loading ? 'Saving…' : 'Set password'}
          </button>
        </form>
        <p className="hint-text">
          Already set up? <Link to="/partner/login">Partner sign in</Link>
        </p>
      </div>
    </div>
  );
}
